import { useCallback, useEffect, useRef, useState } from "react";
import { throttle } from "lodash";
import { useMoment } from "../../../common/hooks/useMoment";
import {
  CursorEntry,
  CursorPointerWebsocketMessageResponse,
  MultiplayerBasePayloadType,
} from "../../../types/Multiplayer.types";
import { useMultiplayer } from "../useMultiplayer";
import { getAvatarColor } from "../../../utils/themeUtils";
import { useDesign } from "../../Design/useDesign";
import { useCollaborators } from "../../Collaborators/useCollaborators";
import { useSelector } from "../../../common/hooks/useSelector";

export interface CursorPointerWebsocketProps {
  userId: string;
  isMobile?: boolean;
  currentVisibleSide?: string;
}

export const useCursorPointerWebsocket = ({
  isMobile,
  currentVisibleSide,
}: CursorPointerWebsocketProps) => {
  const { activeCollaborators } = useCollaborators();
  const userInfo = useSelector((state) => state.userInfo);
  const { designId } = useDesign();
  const { moment } = useMoment();
  const [cursorsOfUsersConnected, setCursorsOfUsersConnected] = useState<
    CursorEntry[]
  >([]);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const {
    isConnected,
    sendCursorPosition,
    onCursorPosition,
    removeCursorPosition,
  } = useMultiplayer();

  // Handle cursor movement
  const sendCoordinates = useCallback(
    ({ x, y, side }: { x: number; y: number; side: string }) => {
      if (isConnected && designId) {
        sendCursorPosition(designId, { x, y, side }).catch((error: Error) => {
          console.error("Failed to send cursor position:", error);
        });
      }
    },
    [isConnected, sendCursorPosition, designId]
  );

  const onCursorMove = throttle(
    (e) => {
      // Don't track cursor on mobile devices
      if (isMobile) return;

      if (previewContainerRef.current) {
        // Find the image element that the cursor is over
        const images = previewContainerRef.current.getElementsByTagName("img");
        let targetImage: HTMLImageElement | null = null;
        let imageSide = "";

        for (const img of Array.from(images)) {
          const rect = img.getBoundingClientRect();
          if (
            e.pageX >= rect.left &&
            e.pageX <= rect.right &&
            e.pageY >= rect.top &&
            e.pageY <= rect.bottom
          ) {
            targetImage = img;
            imageSide = img.getAttribute("alt") || "";
            break;
          }
        }

        // Only send coordinates if cursor is over an image
        if (targetImage) {
          const rect = targetImage.getBoundingClientRect();
          const xRelativePosInsideImage = (e.pageX - rect.left) / rect.width;
          const yRelativePosInsideImage = (e.pageY - rect.top) / rect.height;

          // Store coordinates relative to the image itself, not the container
          sendCoordinates({
            x: xRelativePosInsideImage,
            y: yRelativePosInsideImage,
            side: imageSide,
          });
        }
      }
    },
    32,
    { leading: true }
  );

  useEffect(() => {
    if (!previewContainerRef.current || isMobile) {
      return;
    }
    previewContainerRef.current.addEventListener("mousemove", onCursorMove);
    const copyRef = previewContainerRef.current;
    return () => {
      copyRef.removeEventListener("mousemove", onCursorMove);
    };
  }, [onCursorMove, isMobile]);

  // Handle cursor position updates from other users
  useEffect(() => {
    const handleCursorUpdate = (
      data: MultiplayerBasePayloadType<CursorPointerWebsocketMessageResponse>
    ) => {
      if (data.userId !== userInfo?.id) {
        // On mobile, only show cursors for the currently visible side
        if (
          isMobile &&
          currentVisibleSide &&
          data.side !== currentVisibleSide
        ) {
          return;
        }

        const newCursorEntry = {
          userId: data.userId,
          x: data.x,
          y: data.y,
          side: data.side,
          color: getAvatarColor(data.userId),
        };
        setCursorsOfUsersConnected((prev) => {
          const updatedCursors = [...prev];
          const index = updatedCursors.findIndex(
            (cursor) => cursor.userId === data.userId
          );
          if (index !== -1) {
            updatedCursors[index] = newCursorEntry;
          } else {
            updatedCursors.push(newCursorEntry);
          }
          return updatedCursors;
        });
      }
    };

    onCursorPosition(handleCursorUpdate);
    return () => {
      removeCursorPosition(handleCursorUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    designId,
    moment,
    cursorsOfUsersConnected,
    onCursorPosition,
    userInfo?.id,
    isMobile,
    currentVisibleSide,
  ]);

  // Filter out cursors when users disconnect
  useEffect(() => {
    setCursorsOfUsersConnected((prev) =>
      prev.filter((cursor) =>
        activeCollaborators.some(
          (user) => user.userId === cursor.userId && user.designId === designId
        )
      )
    );
  }, [activeCollaborators, designId]);

  // Filter cursors when visible side changes on mobile
  useEffect(() => {
    if (isMobile && currentVisibleSide) {
      setCursorsOfUsersConnected((prev) =>
        prev.filter((cursor) => cursor.side === currentVisibleSide)
      );
    }
  }, [isMobile, currentVisibleSide]);

  return {
    cursorsOfUsersConnected,
    previewContainerRef,
  };
};
